/**
 * Contest Entry Service
 * 
 * Provides contest entry management functionality including:
 * - Fetching entries (with role-based filtering)
 * - Rating entries (managers only)
 * - Searching and filtering entries
 * 
 * @module ContestEntryService
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  updateDoc,
  setDoc,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  ContestEntry,
  ContestEntryFilters,
  ContestEntrySort,
  RateContestEntryData,
  CreateContestEntryData,
  ManagerRating,
} from '../@types/contestEntry';

const CONTEST_ENTRIES_COLLECTION = 'contest-entries';
const CONTEST_SETTINGS_COLLECTION = 'contest-settings';
const CONTEST_SETTINGS_DOC_ID = 'config';

/**
 * Contest Entry Service Class
 */
export class ContestEntryService {
  /**
   * Get whether contest entry is enabled for sales reps.
   * Defaults to true if no settings document exists.
   */
  static async getContestEnabled(): Promise<boolean> {
    try {
      const settingsRef = doc(db, CONTEST_SETTINGS_COLLECTION, CONTEST_SETTINGS_DOC_ID);
      const settingsSnap = await getDoc(settingsRef);
      if (!settingsSnap.exists()) {
        return true; // Default: contest is open
      }
      const data = settingsSnap.data();
      return data?.enabled !== false;
    } catch (error: any) {
      console.error('Error fetching contest enabled state:', error);
      return true; // Default to enabled on error
    }
  }

  /**
   * Set whether contest entry is enabled (managers only).
   * When false, sales reps see "contest ended" message.
   */
  static async setContestEnabled(enabled: boolean): Promise<void> {
    try {
      const settingsRef = doc(db, CONTEST_SETTINGS_COLLECTION, CONTEST_SETTINGS_DOC_ID);
      await setDoc(settingsRef, {
        enabled,
        updatedAt: Timestamp.now(),
      }, { merge: true });
    } catch (error: any) {
      console.error('Error updating contest enabled state:', error);
      throw new Error(`Failed to update contest settings: ${error.message}`);
    }
  }

  /**
   * Get contest entry by ID
   */
  static async getEntryById(entryId: string): Promise<ContestEntry | null> {
    try {
      const entryRef = doc(db, CONTEST_ENTRIES_COLLECTION, entryId);
      const entrySnap = await getDoc(entryRef);

      if (!entrySnap.exists()) {
        return null;
      }

      return {
        id: entrySnap.id,
        ...entrySnap.data(),
      } as ContestEntry;
    } catch (error: any) {
      console.error('Error fetching contest entry:', error);
      throw new Error(`Failed to fetch contest entry: ${error.message}`);
    }
  }

  /**
   * Get all contest entries (managers see all, sales reps see only their own)
   */
  static async getAllEntries(
    userId: string,
    userRole: 'manager' | 'sales-representative',
    filters?: ContestEntryFilters,
    sort?: ContestEntrySort
  ): Promise<ContestEntry[]> {
    try {
      const entriesRef = collection(db, CONTEST_ENTRIES_COLLECTION);
      const constraints: QueryConstraint[] = [];

      // Role-based filtering: sales reps only see their own entries
      if (userRole === 'sales-representative') {
        constraints.push(where('submittedBy', '==', userId));
      }

      // Apply filters
      if (filters) {
        // Status filter
        if (filters.status) {
          if (Array.isArray(filters.status)) {
            constraints.push(where('status', 'in', filters.status));
          } else {
            constraints.push(where('status', '==', filters.status));
          }
        }

        // Submitter filter
        if (filters.submittedBy) {
          constraints.push(where('submittedBy', '==', filters.submittedBy));
        }

        // Store filter
        if (filters.storeId) {
          constraints.push(where('storeId', '==', filters.storeId));
        }

        // Rated filter
        if (filters.rated !== undefined) {
          if (filters.rated) {
            constraints.push(where('rating', '!=', null));
          } else {
            constraints.push(where('rating', '==', null));
          }
        }

        // Date range filters
        if (filters.dateFrom) {
          constraints.push(where('submittedAt', '>=', Timestamp.fromDate(filters.dateFrom)));
        }
        if (filters.dateTo) {
          constraints.push(where('submittedAt', '<=', Timestamp.fromDate(filters.dateTo)));
        }
      }

      // For sales reps filtering by submittedBy, we need to sort client-side to avoid index requirement
      // Firebase requires a composite index when filtering by one field and ordering by another
      const needsClientSideSort = userRole === 'sales-representative';
      
      if (!needsClientSideSort) {
        // Apply sorting for managers (no submittedBy filter, so no index needed)
        const sortField = sort?.field || 'submittedAt';
        const sortDirection = sort?.direction || 'desc';
        constraints.push(orderBy(sortField, sortDirection));
      }

      const q = query(entriesRef, ...constraints);
      const querySnapshot = await getDocs(q);

      const entries: ContestEntry[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        entries.push({
          id: doc.id,
          ...data,
        } as ContestEntry);
      });

      // Apply client-side sorting for sales reps (to avoid index requirement)
      let sortedEntries = entries;
      if (needsClientSideSort) {
        const sortField = sort?.field || 'submittedAt';
        const sortDirection = sort?.direction || 'desc';
        sortedEntries = [...entries].sort((a, b) => {
          let aValue: any;
          let bValue: any;
          
          switch (sortField) {
            case 'submittedAt':
              aValue = a.submittedAt instanceof Date ? a.submittedAt : (a.submittedAt as any)?.toDate?.() || new Date(0);
              bValue = b.submittedAt instanceof Date ? b.submittedAt : (b.submittedAt as any)?.toDate?.() || new Date(0);
              break;
            case 'storeName':
              aValue = a.storeName || '';
              bValue = b.storeName || '';
              break;
            case 'submittedByName':
              aValue = a.submittedByName || '';
              bValue = b.submittedByName || '';
              break;
            case 'rating':
              aValue = a.rating || 0;
              bValue = b.rating || 0;
              break;
            case 'status':
              aValue = a.status || '';
              bValue = b.status || '';
              break;
            default:
              return 0;
          }
          
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      // Apply client-side filters for complex queries (rating range, search term)
      let filteredEntries = sortedEntries;

      if (filters) {
        // Search term filter (client-side)
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filteredEntries = filteredEntries.filter(
            (entry) =>
              entry.storeName?.toLowerCase().includes(searchLower) ||
              entry.storeCustomerNo?.toLowerCase().includes(searchLower) ||
              entry.submittedByName?.toLowerCase().includes(searchLower) ||
              entry.message?.toLowerCase().includes(searchLower)
          );
        }

        // Rating range filters (client-side)
        if (filters.minRating !== undefined) {
          filteredEntries = filteredEntries.filter(
            (entry) => entry.rating !== undefined && entry.rating >= filters.minRating!
          );
        }
        if (filters.maxRating !== undefined) {
          filteredEntries = filteredEntries.filter(
            (entry) => entry.rating !== undefined && entry.rating <= filters.maxRating!
          );
        }
      }

      return filteredEntries;
    } catch (error: any) {
      console.error('Error fetching contest entries:', error);
      throw new Error(`Failed to fetch contest entries: ${error.message}`);
    }
  }

  /**
   * Rate a contest entry (managers only)
   * Supports multiple managers rating the same entry
   */
  static async rateEntry(
    entryId: string,
    ratingData: RateContestEntryData,
    managerId: string,
    managerName: string
  ): Promise<void> {
    try {
      const entryRef = doc(db, CONTEST_ENTRIES_COLLECTION, entryId);
      
      // Get current entry to access existing ratings
      const entrySnap = await getDoc(entryRef);
      if (!entrySnap.exists()) {
        throw new Error('Contest entry not found');
      }
      
      const currentData = entrySnap.data();
      const existingRatings: ManagerRating[] = currentData.ratings || [];
      
      console.log('[ContestEntry] ===== RATING SUBMISSION =====');
      console.log('[ContestEntry] Manager ID:', managerId);
      console.log('[ContestEntry] Manager Name:', managerName);
      console.log('[ContestEntry] New rating value:', ratingData.rating);
      console.log('[ContestEntry] Existing ratings before update:', existingRatings.map(r => ({ 
        id: r.managerId, 
        name: r.managerName, 
        rating: r.rating 
      })));
      
      // Check if this manager has already rated - update their rating
      const existingRatingIndex = existingRatings.findIndex(
        (r: ManagerRating) => r.managerId === managerId
      );
      
      console.log('[ContestEntry] Existing rating index for this manager:', existingRatingIndex);
      
      // Create new manager rating object (only include notes if provided)
      const newManagerRating: ManagerRating = {
        managerId,
        managerName,
        rating: ratingData.rating,
        ...(ratingData.notes && { notes: ratingData.notes }),
        ratedAt: Timestamp.now(),
      };
      
      // Update or add the rating
      let updatedRatings: ManagerRating[];
      if (existingRatingIndex >= 0) {
        // Update existing rating
        console.log('[ContestEntry] Updating existing rating at index', existingRatingIndex);
        updatedRatings = [...existingRatings];
        updatedRatings[existingRatingIndex] = newManagerRating;
      } else {
        // Add new rating
        console.log('[ContestEntry] Adding new rating to array');
        updatedRatings = [...existingRatings, newManagerRating];
      }
      
      console.log('[ContestEntry] Updated ratings array length:', updatedRatings.length);
      
      // Calculate average rating
      console.log('[ContestEntry] Calculating average rating...');
      console.log('[ContestEntry] Updated ratings array:', updatedRatings.map(r => ({ name: r.managerName, rating: r.rating })));
      
      const totalRating = updatedRatings.reduce((sum, r) => sum + r.rating, 0);
      const averageRating = totalRating / updatedRatings.length;
      
      console.log('[ContestEntry] Total rating:', totalRating);
      console.log('[ContestEntry] Number of ratings:', updatedRatings.length);
      console.log('[ContestEntry] Average rating:', averageRating);
      console.log('[ContestEntry] Rounded average:', Math.round(averageRating * 10) / 10);
      
      const updateData: any = {
        ratings: updatedRatings,
        averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
        totalRatings: updatedRatings.length,
        updatedAt: Timestamp.now(),
        // Keep legacy fields for backwards compatibility (use first/latest rating)
        rating: Math.round(averageRating),
        ratedBy: managerId,
        ratedByName: managerName,
        ratedAt: Timestamp.now(),
      };

      // Only add status if provided
      if (ratingData.status) {
        updateData.status = ratingData.status;
      }
      
      // Only add notes if provided (for legacy compatibility)
      if (ratingData.notes) {
        updateData.notes = ratingData.notes;
      }

      await updateDoc(entryRef, updateData);
      
      console.log(`[ContestEntry] Entry ${entryId} rated by ${managerName}: ${ratingData.rating}/10. Average: ${averageRating.toFixed(1)}/10 (${updatedRatings.length} ratings)`);
    } catch (error: any) {
      console.error('Error rating contest entry:', error);
      throw new Error(`Failed to rate contest entry: ${error.message}`);
    }
  }
  
  /**
   * Get a manager's rating for a specific entry
   */
  static getManagerRating(entry: ContestEntry, managerId: string): ManagerRating | undefined {
    return entry.ratings?.find(r => r.managerId === managerId);
  }
  
  /**
   * Check if a manager has rated an entry
   */
  static hasManagerRated(entry: ContestEntry, managerId: string): boolean {
    return entry.ratings?.some(r => r.managerId === managerId) || false;
  }

  /**
   * Update contest entry status
   */
  static async updateEntryStatus(
    entryId: string,
    status: ContestEntry['status']
  ): Promise<void> {
    try {
      const entryRef = doc(db, CONTEST_ENTRIES_COLLECTION, entryId);
      await updateDoc(entryRef, {
        status,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      console.error('Error updating contest entry status:', error);
      throw new Error(`Failed to update contest entry status: ${error.message}`);
    }
  }
}

